import { colors, font } from '@/theme/zeelool.theme';
import { Box, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';

// 自定义样式组件
const PCReviewItem = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  borderBottom: '1px solid #eee',
  padding: '25px 0',
});

const PCLeftBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexDirection: 'column',
});

const PCRightBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexDirection: 'column',
  height: 'auto',
  minHeight: '8.14vw',
  marginLeft: '168px',
});

const PCStarBox = styled(Box)({
  marginBottom: '12px',
  '& .nick-name': {
    display: 'block',
    fontFamily: font.bb,
    fontSize: '16px',
    color: colors.primaryBlack,
    lineHeight: '19px',
    textAlign: 'left',
    fontStyle: 'normal',
    textTransform: 'none',
  },
  '& .star': {
    marginBottom: '8px',
    '& .MuiRating-icon': {
      fontSize: '18px',
    },
  },
});

const PCColorBox = styled(Box)({
  fontFamily: font.br,
  fontWeight: 'normal',
  fontSize: '14px',
  color: colors.auxiliaryText,
  lineHeight: '17px',
  textAlign: 'left',
  fontStyle: 'normal',
  textTransform: 'none',
  marginBottom: '16px',
});

const PCReviewText = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexDirection: 'column',
  '& .review-title': {
    fontFamily: font.bb,
    fontWeight: 'normal',
    fontSize: '20px',
    color: colors.primaryBlack,
    lineHeight: '20px',
    textAlign: 'left',
    fontStyle: 'normal',
    textTransform: 'none',
  },
  '& .review-content': {
    fontFamily: font.ir,
    fontWeight: 400,
    fontSize: '14px',
    color: colors.primaryBlack,
    lineHeight: '22px',
    textAlign: 'left',
    fontStyle: 'normal',
    textTransform: 'none',
    marginTop: '16px',
    whiteSpace: 'pre-line',
  },
});

const PCActionBtn = styled(Box)({
  marginTop: '24px',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  '& .iconfont': {
    cursor: 'pointer',
    width: '16px',
  },
  '& .helpful': {
    fontFamily: font.br,
    fontWeight: 'normal',
    fontSize: '14px',
    color: colors.primaryBlack,
    lineHeight: '17px',
    textAlign: 'left',
    fontStyle: 'normal',
    textTransform: 'none',
    paddingLeft: '10px',
    cursor: 'pointer',
    width: '100px',
  },
  '& .facebook': {
    width: '16px',
    height: '16px',
    marginLeft: '26px',
  },
  '& .review-date': {
    fontFamily: font.ir,
    fontWeight: 'normal',
    fontSize: '14px',
    color: colors.auxiliaryText,
    lineHeight: '17px',
    textAlign: 'left',
    fontStyle: 'normal',
    textTransform: 'none',
    marginLeft: '8px',
  },
});

interface CustomerReviewsSkeletonProps {
  isWeb?: boolean;
  count?: number;
}

// PC端骨架屏组件
const PCReviewSkeleton = ({ count = 3 }: { count?: number }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <PCReviewItem key={index}>
        <PCLeftBox>
          <PCStarBox>
            <Skeleton variant="rectangular" width={120} height={18} className="mb-[8px]" />
            <Skeleton variant="text" width={80} height={19} />
          </PCStarBox>
          <PCColorBox>
            <Skeleton variant="text" width={60} height={17} />
            <Skeleton variant="text" width={50} height={17} />
          </PCColorBox>
          <Box className="flex flex-col justify-between">
            {[1, 2, 3].map((itemIndex) => (
              <Box
                key={itemIndex}
                className="flex items-center justify-between w-[257px] min-w-[257px] mb-[10px] last:mb-0"
              >
                <Skeleton variant="text" width={50} height={17} />
                <Skeleton variant="rectangular" width={165} height={6} className="mx-[5px]" />
                <Skeleton variant="text" width={30} height={17} />
              </Box>
            ))}
          </Box>
        </PCLeftBox>
        <PCRightBox>
          <PCReviewText>
            <Skeleton variant="text" width={200} height={20} />
            <Skeleton variant="text" width="100%" height={22} />
            <Skeleton variant="text" width="80%" height={22} />
            <Skeleton variant="text" width="60%" height={22} />
          </PCReviewText>
          <Box className="flex gap-[10px] mt-[20px]">
            {[1, 2, 3].map((imgIndex) => (
              <Skeleton key={imgIndex} variant="rectangular" width={120} height={120} />
            ))}
          </Box>
          <PCActionBtn>
            <Skeleton variant="rectangular" width={16} height={16} />
            <Skeleton variant="text" width={80} height={17} className="ml-[10px]" />
            <Skeleton variant="rectangular" width={16} height={16} className="ml-[26px]" />
            <Skeleton variant="text" width={60} height={17} className="ml-[8px]" />
          </PCActionBtn>
        </PCRightBox>
      </PCReviewItem>
    ))}
  </>
);

// 移动端骨架屏组件
const MobileReviewSkeleton = ({ count = 3 }: { count?: number }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <Box key={index} className="bg-white rounded-[10px] p-[12px] mb-[8px]">
        <Box>
          <Box className="flex items-center justify-between mb-[3px]">
            <Skeleton variant="text" width={60} height={12} />
            <Box className="flex items-center">
              <Skeleton variant="rectangular" width={12} height={12} className="mr-[4px]" />
              <Skeleton variant="text" width={40} height={12} />
            </Box>
          </Box>
          <Skeleton variant="rectangular" width={80} height={16} />
        </Box>
        <Skeleton variant="text" width="60%" height={16} className="mt-[8px] mb-[6px]" />
        <Box className="flex gap-[8px] py-6">
          {[1, 2, 3].map((tagIndex) => (
            <Skeleton key={tagIndex} variant="rectangular" width={64} height={24} />
          ))}
        </Box>
        <Skeleton variant="text" width="100%" height={20} className="pt-8 pb-6" />
        <Box className="flex gap-[8px] py-6">
          {[1, 2, 3, 4].map((imgIndex) => (
            <Skeleton key={imgIndex} variant="rectangular" width={64} height={64} />
          ))}
        </Box>
        <div className="flex items-center justify-between pt-6">
          <div className="flex items-center gap-[20px]">
            <Skeleton variant="text" width={50} height={12} />
            <Skeleton variant="text" width={40} height={12} />
          </div>
          <div className="flex items-center">
            <Skeleton variant="rectangular" width={16} height={16} className="mr-[4px]" />
            <Skeleton variant="text" width={60} height={12} />
          </div>
        </div>
      </Box>
    ))}
  </>
);

const CustomerReviewsSkeleton: React.FC<CustomerReviewsSkeletonProps> = ({
  isWeb = false,
  count = 3,
}) => {
  return isWeb ? <PCReviewSkeleton count={count} /> : <MobileReviewSkeleton count={count} />;
};

// 移动端折叠区域骨架屏（镜片选择、服务保障、赠品等）
export const WapCollapseSkeleton = ({ itemCount = 6 }: { itemCount?: number }) => (
  <div className="bg-white border-t border-[#eeeeee]">
    <div className="flex items-center justify-between h-[50px] px-[14px]">
      <Skeleton variant="text" width={160} height={18} />
      <Skeleton variant="circular" width={20} height={20} />
    </div>
    <div className="grid grid-cols-2 gap-x-[10px] gap-y-[12px] px-[14px] pb-[16px]">
      {Array.from({ length: itemCount }).map((_, i) => (
        <div key={i} className="flex items-center">
          <Skeleton variant="rectangular" width={14} height={14} className="mr-[6px] shrink-0" />
          <Skeleton variant="text" width={80 + (i % 3) * 20} height={16} />
        </div>
      ))}
    </div>
  </div>
);

// 移动端商品详情骨架屏（尺寸图+参数表）
export const WapProductDetailsSkeleton = () => (
  <div className="bg-white border-t border-[#eeeeee]">
    <div className="flex items-center justify-between h-[50px] px-[14px]">
      <Skeleton variant="text" width={120} height={18} />
      <Skeleton variant="circular" width={20} height={20} />
    </div>
    <div className="px-[14px] pb-[16px]">
      <Skeleton variant="rectangular" width="100%" height={200} className="rounded-[4px]" />
      <div className="mt-[12px] flex justify-center">
        <Skeleton variant="rectangular" width={100} height={24} className="rounded-[12px]" />
      </div>
      <div className="mt-[12px] grid grid-cols-3 gap-[8px]">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col items-center">
            <Skeleton variant="text" width={60} height={14} />
            <Skeleton variant="text" width={50} height={14} />
          </div>
        ))}
      </div>
      <div className="mt-[12px] flex justify-center">
        <Skeleton variant="rectangular" width={120} height={32} className="rounded-[4px]" />
      </div>
    </div>
  </div>
);

export default CustomerReviewsSkeleton;
